import { useApplicationModals } from "app/common/hooks";
import { ModalMolecule } from "app/components/molecules/modal";
import api from "app/integration/api";
import { useEffect, useMemo, useState } from "react";

/**
 * Rincian unit barcode unik satu varian, dikelompokkan per gudang.
 *
 * Hanya berguna untuk barang PER PCS: setiap baris `product_codes` adalah satu unit
 * fisik dengan siklus hidupnya sendiri (`status`). Barang per-qty tidak punya baris
 * ini sama sekali, jadi pemanggilnya hanya membuka modal ini untuk barang unik.
 *
 * Nama gudang dipetakan dari payload, BUKAN dari respons API: `ProductCodeService`
 * tidak menyertakan relasi warehouse, jadi respons hanya membawa `warehouse_id`.
 * Halaman laporan sudah menerima daftar gudang dari endpointnya sendiri, sehingga
 * pemetaannya bisa lokal tanpa menambah query maupun mengubah backend.
 */
const STATUS_LABEL = {
  warehouse: { text: "Di gudang", cls: "bg-green-100 text-green-700" },
  delivery: { text: "Di outlet", cls: "bg-blue-100 text-blue-700" },
  packing: { text: "Packing", cls: "bg-yellow-100 text-yellow-800" },
  return: { text: "Retur", cls: "bg-orange-100 text-orange-800" },
  scrap: { text: "Scrap", cls: "bg-red-100 text-red-700" },
};

// Status yang berarti unitnya masih ADA dan bisa dijual/dikirim. `packing` sengaja
// TIDAK dihitung tersedia: unitnya sudah dialokasikan ke sebuah pesanan.
const AVAILABLE = ["warehouse", "delivery"];

function ViewProductCodes() {
  const { showStates, getPayloads, closeModal } = useApplicationModals();
  const payload = getPayloads("productCodes.view");
  const variantId = payload?.product_variant_id;
  const productName = payload?.product_name;
  const warehouses = payload?.warehouses ?? [];

  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const show = showStates["productCodes.view"];

  useEffect(() => {
    if (!show || !variantId) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    api.productCode
      .index({ product_variant_id: variantId, limit: 1000 })
      .then((res) => {
        if (cancelled) return;
        setCodes(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setError("Gagal memuat rincian barcode.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, variantId]);

  const warehouseName = (id) => {
    // `warehouse_id NULL` = Gudang Utama, gudang VIRTUAL yang tidak punya baris di
    // tabel `warehouses`. Ini konvensi yang sama dengan otoritas stok di seluruh
    // sistem, dan menyangkut hampir semua unit: `ReceiveOrderItemService` membuat
    // baris `product_codes` TANPA menyetel `warehouse_id`, jadi unit hasil penerimaan
    // barang selalu NULL sampai ia dipindah lewat transfer gudang.
    if (id === null || id === undefined) return "Gudang Utama";
    const found = warehouses.find((w) => w.warehouse_id === id);
    return found ? found.warehouse_name : `Gudang #${id}`;
  };

  // Dikelompokkan per gudang supaya pertanyaan "unit mana yang ada di gudang mana"
  // terjawab langsung, bukan dengan menyisir daftar panjang.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const c of codes) {
      const key = c.warehouse_id ?? "null";
      if (!map.has(key)) map.set(key, { warehouse_id: c.warehouse_id ?? null, rows: [] });
      map.get(key).rows.push(c);
    }
    return [...map.values()].sort((a, b) => {
      if (a.warehouse_id === null) return -1;
      if (b.warehouse_id === null) return 1;
      return a.warehouse_id - b.warehouse_id;
    });
  }, [codes]);

  const availableCount = codes.filter((c) => AVAILABLE.includes(c.status)).length;

  return (
    <ModalMolecule
      show={show}
      title={`Rincian Barcode — ${productName ?? "Item"}`}
      onClose={() => closeModal("productCodes.view")}
      size="lg"
    >
      {loading ? (
        <p className="text-sm text-gray-500 py-6 text-center">Memuat…</p>
      ) : error ? (
        <p className="text-sm text-red-600 py-6 text-center">{error}</p>
      ) : codes.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center italic">
          Belum ada unit barcode untuk item ini.
        </p>
      ) : (
        <div className="max-h-[60vh] overflow-y-auto">
          <div className="mb-3 text-sm text-gray-600">
            Total unit: <strong>{codes.length}</strong> &middot; tersedia (di gudang /
            di outlet): <strong>{availableCount}</strong>
          </div>
          {/*
            Tanpa keterangan ini, "Gudang Utama" pada hampir semua unit terbaca seperti
            data lokasi yang hilang. Ia justru keadaan normal: unit baru diterima memang
            berada di gudang pusat sampai dipindahkan.
          */}
          <p className="mb-3 text-xs text-gray-500 italic">
            Unit hasil penerimaan barang berada di Gudang Utama sampai dipindahkan lewat
            transfer gudang ke outlet.
          </p>

          {grouped.map((g) => (
            <div key={String(g.warehouse_id)} className="mb-4">
              <div className="flex items-center justify-between border-b border-gray-200 pb-1 mb-2">
                <h4 className="font-semibold text-gray-800">{warehouseName(g.warehouse_id)}</h4>
                <span className="text-xs text-gray-500">
                  {g.rows.filter((r) => AVAILABLE.includes(r.status)).length} tersedia dari{" "}
                  {g.rows.length} unit
                </span>
              </div>
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500">
                    <th className="py-1 pr-2">Barcode (QR)</th>
                    {/*
                      Lokasi diulang per BARIS, bukan cuma sebagai judul kelompok:
                      daftarnya bisa panjang dan bergulir, sehingga judul kelompok
                      hilang dari pandangan dan pembaca kehilangan konteks unit yang
                      sedang dilihatnya.
                    */}
                    <th className="py-1 pr-2">Lokasi</th>
                    <th className="py-1 pr-2">Status</th>
                    <th className="py-1 pr-2">Ref</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((c) => {
                    const st = STATUS_LABEL[c.status] ?? {
                      text: c.status ?? "-",
                      cls: "bg-gray-100 text-gray-700",
                    };
                    return (
                      <tr key={c.id} className="border-t border-gray-100">
                        <td className="py-1 pr-2 font-mono text-xs break-all">{c.qr_code}</td>
                        <td className="py-1 pr-2 whitespace-nowrap">
                          {warehouseName(c.warehouse_id)}
                          {c.status === "delivery" && (
                            <span className="ml-1 text-xs text-gray-500">(di outlet)</span>
                          )}
                        </td>
                        <td className="py-1 pr-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${st.cls}`}>
                            {st.text}
                          </span>
                        </td>
                        <td className="py-1 pr-2 text-xs text-gray-500">{c.ref ?? "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </ModalMolecule>
  );
}

export default ViewProductCodes;
